import { ComponentPropsWithoutRef } from "react";
import { css, keyframes } from "@emotion/react";
import { FieldGroupContextValue, useFieldGroup } from "../FieldGroup";
import { ShimmerEffectBox } from "../ShimmerEffect";
import { useClickAnimation } from "../hooks/useClickAnimation";
import { useTheme } from "../theme";

export interface RadioProps
  extends ComponentPropsWithoutRef<"input">, FieldGroupContextValue {
  /**
   * ルートにある要素に対するrefを指定します。
   */
  rootRef?: React.Ref<HTMLLabelElement>;
  /**
   * 実体である`input`要素に対するrefを指定します。
   */
  inputRef?: React.Ref<HTMLInputElement>;
  /**
   * ラベルを指定します。これを指定しない場合、ルート要素は`span`要素になります。
   */
  label?: string;
  /**
   * ラベルは、`label` propで指定してください。
   */
  children?: never;
}

const fade = keyframes`
  100% {
      opacity: 0;
  }
`;
const wave = keyframes`
  100% {
      box-shadow: 0 0 0 6px currentcolor;
  }
`;

export const Radio = ({
  inputRef,
  rootRef,
  label,
  style,
  className,
  onClick: propsOnClick,
  loading: propsLoading,
  readOnly: propsReadOnly,
  disabled,
  required,
  ...props
}: RadioProps) => {
  const theme = useTheme();
  const hasChildren = !!label;
  const RootComponent = hasChildren ? "label" : "span";
  const { clicked, onClick, onAnimationEnd } = useClickAnimation(propsOnClick);
  const { loading, readOnly } = useFieldGroup({
    loading: propsLoading,
    readOnly: propsReadOnly,
  });
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const disabledOrReadonly = disabled || readOnly;

  return (
    <RootComponent
      ref={rootRef as unknown as React.Ref<HTMLLabelElement>}
      css={css`
        position: relative;
        box-sizing: border-box;
        display: inline-flex;
        align-items: center;
      `}
      style={style}
      className={className}
    >
      <div
        css={[
          css`
            padding: 8px;
          `,
          disabled
            ? css`
                cursor: not-allowed;
                background-color: ${theme.palette.gray[300]};
                border-radius: 4px;
              `
            : css`
                cursor: pointer;
              `,
          readOnly
            ? css`
                background-color: ${theme.palette.gray[300]};
                border-radius: 4px;
              `
            : null,
        ]}
      >
        {loading ? (
          <ShimmerEffectBox width={24} height={24} borderRadius={4} />
        ) : (
          // type="radio"に、readOnly属性及びaria-readonlyは指定できないが、他のinput要素と同様に扱うために利用している。
          // eslint-disable-next-line jsx-a11y/role-supports-aria-props
          <input
            onAnimationEnd={onAnimationEnd}
            data-clicked={clicked}
            type="radio"
            /**
             * type=radioのinput要素には、readOnly属性が存在しないため
             * aria-readonlyとdisabledの併用で代替しています。
             */
            aria-readonly={readOnly}
            onClick={onClick}
            required={required}
            css={[
              theme.typography.button,
              css`
                --radio-size: 24px;
                position: relative;
                box-sizing: border-box;
                display: flex;
                flex-shrink: 0;
                align-items: center;
                justify-content: center;
                width: var(--radio-size);
                height: var(--radio-size);
                padding: 0px;
                margin: 0px;
                color: ${theme.palette.intentions.primary.contrastText};
                touch-action: manipulation;
                cursor: pointer;
                background-color: white;
                border: 2px solid ${theme.palette.intentions.primary.main};
                border-radius: 50%;
                outline: none;
                transition: background-color
                  ${theme.transitions.duration.shortest}
                  ${theme.transitions.easing.easeIn};
                appearance: none;

                &::before {
                  box-sizing: border-box;
                  display: block;
                  width: 12px;
                  height: 12px;
                  content: "";
                  background-color: ${theme.palette.intentions.primary.main};
                  border-radius: 50%;
                  opacity: 0;
                }
                &:checked::before {
                  opacity: 1;
                }

                &:disabled {
                  color: ${theme.palette.action.disabled};
                  cursor: not-allowed;
                  background-color: ${theme.palette.gray[300]};
                  border-color: ${theme.palette.gray[400]};
                  &:checked {
                    background-color: ${theme.palette.gray[300]};
                    border-color: ${theme.palette.gray[400]};
                    &::before {
                      background-color: ${theme.palette.gray[400]};
                    }
                  }
                }

                &:disabled[aria-readonly="true"] {
                  background-color: white;
                  &:checked {
                    border-color: ${theme.palette.gray[500]};
                    &::before {
                      border-color: white;
                    }
                  }
                }

                &::after {
                  position: absolute;
                  top: -1px;
                  right: -1px;
                  bottom: -1px;
                  left: -1px;
                  z-index: 1;
                  display: block;
                  color: ${theme.palette.intentions.primary.main};
                  pointer-events: none;
                  content: "";
                  border-radius: inherit;
                  opacity: 0.2;
                  transform: scale(1);
                }
                &[data-clicked="true"]::after {
                  animation:
                    ${fade} 2s cubic-bezier(0.08, 0.82, 0.17, 1),
                    ${wave} 0.4s cubic-bezier(0.08, 0.82, 0.17, 1);
                }
              `,
            ]}
            {...props}
            disabled={disabledOrReadonly}
            ref={inputRef}
          />
        )}
      </div>
      {hasChildren ? (
        loading ? (
          <ShimmerEffectBox
            width={120}
            fontSize={theme.typography.body.fontSize}
            height={
              theme.typography.body.fontSize * theme.typography.body.lineHeight
            }
            css={css`
              margin-left: 0.5em;
            `}
          />
        ) : (
          <span
            css={[
              theme.typography.body,
              css`
                margin: 0px 0px 0px 0.5em;
                white-space: inherit;
              `,
              {
                color: disabled ? theme.palette.action.disabled : "inherit",
                cursor: disabledOrReadonly ? "default" : "pointer",
              },
            ]}
          >
            {label}
            {required ? (
              <span
                css={css`
                  color: ${theme.palette.intentions.error.main};
                `}
              >
                *
              </span>
            ) : null}
          </span>
        )
      ) : null}
    </RootComponent>
  );
};
